import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileText,
  LoaderCircle,
  RotateCcw,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { fileFailureKey, formatFileSize } from './chat-file-format'
import type { ComposerFile } from './use-ai-chat-files'
import { Button } from '@/shared/ui/button'
import { cn } from '@/shared/lib/utils'

function ProgressBar({ label, value }: { label: string; value: number }) {
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
      className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"
    >
      <div
        className="h-full bg-primary transition-[width]"
        style={{ width: `${value}%` }}
      />
    </div>
  )
}

function StatusIcon({ status }: { status: ComposerFile['status'] }) {
  if (status === 'ready')
    return (
      <CheckCircle2
        className="size-4 text-emerald-600 dark:text-emerald-400"
        aria-hidden="true"
      />
    )
  if (status === 'failed')
    return (
      <AlertTriangle className="size-4 text-destructive" aria-hidden="true" />
    )

  return (
    <LoaderCircle
      className="size-4 animate-spin text-muted-foreground"
      aria-hidden="true"
    />
  )
}

/** A file waiting in the composer: upload progress, processing and controls. */
export function ComposerFileCard({
  file,
  onRemove,
  onRetry,
}: {
  file: ComposerFile
  onRemove: (key: string) => void
  onRetry: (key: string) => void
}) {
  const { t } = useTranslation()
  const failed = file.status === 'failed'

  return (
    <li
      className={cn(
        'flex items-start gap-3 rounded-md border bg-background p-2 text-sm',
        failed && 'border-destructive/50',
      )}
    >
      <FileText
        className="mt-0.5 size-5 shrink-0 text-muted-foreground"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium" title={file.name}>
          {file.name}
        </p>
        <p
          role="status"
          className={cn(
            'flex items-center gap-1.5 text-xs',
            failed ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          <StatusIcon status={file.status} />
          <span>
            {failed
              ? t(fileFailureKey(file.failureCode))
              : t(`aiChat:files.status.${file.status}`)}
            {file.size ? ` · ${formatFileSize(file.size)}` : ''}
          </span>
        </p>
        {file.status === 'uploading' ? (
          <ProgressBar
            label={t('aiChat:files.uploadProgress', { name: file.name })}
            value={file.progress}
          />
        ) : null}
      </div>
      {failed ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t('aiChat:files.retry', { name: file.name })}
          onClick={() => onRetry(file.key)}
        >
          <RotateCcw aria-hidden="true" />
        </Button>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={t('aiChat:files.remove', { name: file.name })}
        onClick={() => onRemove(file.key)}
      >
        <X aria-hidden="true" />
      </Button>
    </li>
  )
}

/** A file in the conversation. The signed URL is resolved only when opened. */
export function DownloadFileCard({
  fileName,
  size,
  status,
  detail,
  busy,
  onDownload,
  onPreview,
}: {
  fileName: string
  size?: number | null
  status?: string
  detail?: string | null
  busy?: boolean
  onDownload?: () => void
  /** When set, the file name opens an in-app preview. */
  onPreview?: () => void
}) {
  const { t } = useTranslation()

  return (
    <li className="flex items-center gap-3 rounded-md border bg-background p-2 text-sm text-foreground">
      <FileText
        className="size-5 shrink-0 text-muted-foreground"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        {onPreview ? (
          <button
            type="button"
            className="block max-w-full truncate rounded-sm text-left font-medium text-primary underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            title={fileName}
            aria-label={t('aiChat:files.preview', { name: fileName })}
            onClick={onPreview}
          >
            {fileName}
          </button>
        ) : (
          <p className="truncate font-medium" title={fileName}>
            {fileName}
          </p>
        )}
        {status || size ? (
          <p className="text-xs text-muted-foreground">
            {[status, formatFileSize(size)].filter(Boolean).join(' · ')}
          </p>
        ) : null}
        {detail ? (
          <p role="status" className="text-xs text-muted-foreground">
            {detail}
          </p>
        ) : null}
      </div>
      {onDownload ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          aria-label={t('aiChat:files.download', { name: fileName })}
          onClick={onDownload}
        >
          {busy ? (
            <LoaderCircle className="animate-spin" aria-hidden="true" />
          ) : (
            <Download aria-hidden="true" />
          )}
          {t('aiChat:files.downloadAction')}
        </Button>
      ) : null}
    </li>
  )
}
